import React, { useState, useEffect, useRef } from "react";
import { Loading } from "../Components/Loading/Loading";
import { AddNotes } from "../Components/ReleaseNotes/AddNotes"; // Import the Modal component

export const ReleaseNotes = () => {
  const [releases, setReleases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null); // Track last clicked date
  const [isModalOpen, setIsModalOpen] = useState(false); // State to manage modal visibility
  const dateRefs = useRef({});
  const mainContentRef = useRef(null);
  const authToken = localStorage.getItem("authToken");

  useEffect(() => {
        if (!authToken) {
      window.location.href = process.env.REACT_APP_JBM_LOGIN_URL;
      return;
    }
    const fetchReleaseNotes = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/API/V2/release-notes`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${authToken}`,
            },
          }
        );
        if (!response.ok) {
          throw new Error("Failed to fetch release notes");
        }
        const data = await response.json();
        setReleases(Array.isArray(data) ? data : []);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchReleaseNotes();
  }, []);

  const scrollToDate = (date) => {
    setSelectedDate(date); // Update selected date
    const element = dateRefs.current[date];
    if (element) {
      const headerOffset = 80; // Adjust this value based on your layout
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition =
        elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  };

  const getTagColor = (tag) => {
    const colors = {
      "New Feature": "bg-blue-100 text-blue-800",
      "UI/UX": "bg-purple-100 text-purple-800",
      Enhancement: "bg-green-100 text-green-800",
      "Bug Fix": "bg-red-100 text-red-800",
      Performance: "bg-yellow-100 text-yellow-800",
      Security: "bg-gray-100 text-gray-800",
    };
    return colors[tag] || "bg-gray-100 text-gray-800";
  };

  const openModal = () => {
    setIsModalOpen(true); // Open the modal
  };

  const closeModal = () => {
    setIsModalOpen(false); // Close the modal
  };

  if (loading) {
    return <Loading />;
  }
  if (error)
    return <p className="text-center mt-10 text-red-500">Error: {error}</p>;

  return (
    <div className="flex min-h-screen">
      {/* Side Panel */}
      <div className="w-56 min-h-screen p-8 border-r border-gray-200 overflow-y-auto">
        <h2 className="text-lg font-semibold mb-4">Release Dates</h2>
        <ul className="space-y-1.5 mb-12">
          {releases.map((release) => (
            <li key={release.date}>
              <button
                onClick={() => scrollToDate(release.date)}
                className={`text-left tracking-tight leading-none w-full py-1 
                  ${
                    selectedDate === release.date
                      ? "text-blue-800 underline"
                      : "hover:text-blue-800 hover:underline"
                  }`}
              >
                {new Date(release.date).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </button>
            </li>
          ))}
        </ul>
      </div>

      {/* Main Content */}
      <div className="flex-1 p-8 bg-white" ref={mainContentRef}>
        <div className="flex gap-4 items-center mb-6">
          <h1 className="text-3xl font-black tracking-tight leading-tight">
            Release Notes
          </h1>
          {/* <div
            className="flex items-center justify-center cursor-pointer text-lg pb-0.5 font-black h-6 w-6 border border-2 rounded-3xl border-black"
            onClick={openModal}
          >
            +
          </div> */}
        </div>

        {releases.map((release) => (
          <div
            key={release.date}
            ref={(el) => (dateRefs.current[release.date] = el)}
            className="mb-12 pt-4"
          >
            <h2 className="text-xl font-black tracking-tight leading-tight mb-6">
              {new Date(release.date).toLocaleDateString("en-US", {
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </h2>
            <div className="space-y-4">
              {release.notes.map((change, index) => (
                <div key={index} className="flex items-start">
                  <p
                    className={`inline-block w-28 text-center px-3 py-1 rounded-full text-sm font-medium mr-4 ${getTagColor(
                      change.tag
                    )}`}
                  >
                    {change.tag}
                  </p>
                  <p className="text-gray-700 flex-1">{change.description}</p>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      {/* Modal Component */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/20">
          <AddNotes onClose={closeModal} />
        </div>
      )}
    </div>
  );
};
